import { Checkbox, IconButton, Tooltip } from "@mui/material";
import { cloneDeep, get, set, unset } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  AGGREGATOR_FEATURES_MAP,
  OUTLET_ACTIVITY_TYPES,
  OUTLET_STATUSES,
} from "../../../data/outlet-status-constants";
import { convertArrayToObject } from "../../../helpers/catalog-helper";
import {
  getAggregatorLogo,
  isAggregatorFeatureAvailable,
  showOutletMenuIcon,
} from "../../../helpers/outlet-status-helper";
import { getFormattedDateTime } from "../../../helpers/text-format-helper";
import { getOutletOnlineStatus } from "../../../redux/slices";
import "./style.scss";
import circular_loader from "../../../assets/images/circular-loader.gif";
import { isAdminUser } from "../../../helpers/user-info-helpers";
import _ from "lodash";
import { isFeatureAllowed } from "../../../helpers/common-helper";

interface Props {
  parentClass?: string;
  outletData: any;
  setActivityLogModalData: any;
  setOutletToggleModalData?: any;
  setMenuSyncModalData?: any;
  bulkActionConfig: any;
  setBulkActionConfig: any;
  updateMoreOptionsMenuData: any;
  isMoreOptionsMenuOpen: boolean;
  updatedOutletsConfig: any;
  statusLoading: boolean;
}
const OutletListItem = (props: Props) => {
  const {
    parentClass = "",
    outletData,
    setActivityLogModalData,
    setOutletToggleModalData,
    setMenuSyncModalData,
    bulkActionConfig,
    setBulkActionConfig,
    updateMoreOptionsMenuData,
    isMoreOptionsMenuOpen,
    updatedOutletsConfig,
    statusLoading,
  } = props;
  const [metaProvidersDataMap, setMetaProvidersDataMap] = useState<any>({}),
    [isOutletSelected, setIsOutletSelected] = useState<boolean>(false),
    [isRefreshing, setIsRefreshing] = useState(false);
  const { outletsData, outletOnlineStatusData } = useSelector((state: any) => ({
    outletsData: state.outletStatusReducer.outletsData,
    outletOnlineStatusData: state.outletStatusReducer.outletOnlineStatusData,
  }));
  const lastActivityText =
    OUTLET_ACTIVITY_TYPES[outletData?.lastActivityStatus] ||
    outletData?.lastActivityStatus;

  const dispatch = useDispatch();

  useEffect(() => {
    if (outletsData?.metaProviders?.length) {
      let dataToUpdate = convertArrayToObject(outletsData.metaProviders, "mid");
      setMetaProvidersDataMap(dataToUpdate);
    }
  }, [outletsData?.metaProviders]);

  useEffect(() => {
    setIsOutletSelected(
      !!get(
        bulkActionConfig,
        `selectedOutletsMap.${outletData?.brid}.${outletData?.id}`
      )
    );
  }, [bulkActionConfig]);

  const handleMoreOptionsIconClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    updateMoreOptionsMenuData({ anchorEl: event.currentTarget, outletData });
  };

  const showActivityLog = () => {
    setActivityLogModalData({
      show: true,
      outletId: outletData?.id,
      channelDefName: outletData?.channelDefName,
    });
  };

  const onOutletToggleIconClick = () => {
    if(isFeatureAllowed('AGGREGATOR_OUTLET_UPDATE')) {
      setOutletToggleModalData({
        show: true,
        outletData,
      });
    }
  };

  const onMenuSyncIconClick = () => {
    setMenuSyncModalData({
      show: true,
      outletData,
    });
  };

  const refreshOutletStatus = () => {
    let dataToPost = {
      brid: outletData?.brid,
      outletId: outletData?.id,
      type: outletData?.type,
    };
    const callback = (success: Record<string, string>) => {
      if (success.active) {
      }
      setIsRefreshing(false);
    };
    setIsRefreshing(true);
    dispatch(getOutletOnlineStatus({ dataToPost, callback }));
  };

  const onOutletItemCheckboxClick = () => {
    let dataToUpdate = bulkActionConfig?.selectedOutletsMap
      ? cloneDeep(bulkActionConfig?.selectedOutletsMap)
      : {};
    if (dataToUpdate?.[outletData?.brid]?.[outletData?.id]) {
      unset(dataToUpdate, `${outletData?.brid}.${outletData?.id}`);
    } else {
      set(dataToUpdate, `${outletData?.brid}.${outletData?.id}`, outletData);
    }
    setBulkActionConfig((prevVal: any) => ({
      ...prevVal,
      selectedOutletsMap: dataToUpdate,
    }));
  };

  function renderLastActivityInfo() {
    let lastActivityText =
      OUTLET_ACTIVITY_TYPES[outletData?.lastActivityStatus] ||
      outletData?.lastActivityStatus;
    return (
      <div className="w-[calc(45%-20px)] sm:w-full sm:mt-[22px]">
        <div className="flex items-center">
          {outletData?.lastActivityStatus ? (
            <>
              <p className="text-12 text-[#7F7F7F]">Last Activity: </p>
              <div
                className={`ml-[2px] rista-sprite min-w-[16px] ${
                  outletData?.isLastActivitySuccess
                    ? "ic-circle-tick-lightgreen"
                    : "ic-circle-cross-red"
                }`}
                title={`${lastActivityText} ${
                  outletData?.isLastActivitySuccess ? "Success" : "Failed"
                }`}
              ></div>
              <p className="text-14 font-medium">{lastActivityText}</p>
            </>
          ) : (
            <p className="text-12 text-[#7F7F7F]">
              No activity on this outlet.
            </p>
          )}
        </div>
        <p className="text-12 text-[#4B4B4B]">
          {outletData?.lastActivityPerformedBy
            ? `by ${outletData?.lastActivityPerformedBy} `
            : ""}
          {outletData?.lastActivityModifiedDate
            ? `on ${getFormattedDateTime(outletData?.lastActivityModifiedDate)}`
            : ""}
        </p>
      </div>
    );
  }

  function renderLastSyncInfo() {
    let lastSyncSuccessDate = outletData?.lastSyncSuccessDate;
    return (
      <div className="w-[calc(45%-20px)] sm:w-full sm:mt-[22px]">
        <div className="flex items-center">
          {lastSyncSuccessDate ? (
            <>
              <p className="text-13 font-medium">
                <span className="opacity-60">Last Synced on </span>
                {getFormattedDateTime(lastSyncSuccessDate)}
              </p>
            </>
          ) : (
            <p className="text-12 text-[#7F7F7F]">No Successful Menu Sync</p>
          )}
        </div>
      </div>
    );
  }

  function renderOutletOnlineStatusInfo() {
    let currentStatus =
        outletOnlineStatusData?.[outletData?.id]?.currentStatus ||
        outletData?.currentStatus,
      lastChecked =
        outletOnlineStatusData?.[outletData?.id]?.lastChecked ||
        outletData?.lastModifiedOutletDate;
    return (
      <div className="w-[calc(25%-20px)] sm:w-[30%]">
        {isRefreshing || statusLoading ? (
          <div className="flex items-center">
            <p className="text-13 text-black/50">Checking Status…</p>
            <img src={circular_loader} alt="loader" width={24} height={24} />
          </div>
        ) : (
          <>
            <div className="flex items-center">
              <p
                className={`text-14 ${
                  currentStatus === OUTLET_STATUSES.ACTIVE ||
                  currentStatus === OUTLET_STATUSES.ONLINE
                    ? "text-[#3AA537] font-bold"
                    : currentStatus === OUTLET_STATUSES.INACTIVE ||
                      currentStatus === OUTLET_STATUSES.OFFLINE
                    ? "text-[#DD5353] font-bold"
                    : "text-black/40"
                }`}
              >
                {currentStatus
                  ? _.startCase(_.toLower(currentStatus))
                  : "Not Available"}
              </p>
              {isAggregatorFeatureAvailable(
                AGGREGATOR_FEATURES_MAP.refresh_cta.id,
                outletData?.type,
                outletData?.webhookSourceType
              ) && !bulkActionConfig?.active ? (
                <div
                  className="rista-sprite ic-refresh-black ml-[10px] min-w-[20px] hover:rounded-[50%] hover:bg-[#DDDDDD]"
                  onClick={refreshOutletStatus}
                  title="Refresh"
                ></div>
              ) : null}
            </div>
            <p className="text-12 text-[#4B4B4B]">
              {lastChecked
                ? `Last checked ${getFormattedDateTime(lastChecked)}`
                : ""}
            </p>
          </>
        )}
      </div>
    );
  }

  function renderOutletLogoAndInfo() {
    let isOutletUpdated = !!get(
      updatedOutletsConfig,
      `updatedOutlets.${outletData?.brid}.${outletData?.id}`
    );
    return (
      <div
        className={`mr-[20px] w-[calc(55%)] flex items-center border-r border-black/20 pr-[8px]
        sm:w-full sm:border-none sm:pr-0 sm:mr-0
      `}
      >
        <img
          src={getAggregatorLogo(outletData?.type || "")}
          alt="logo-icon"
          className="mr-[12px]"
        />
        <div className="w-[calc(100%-60px)]">
          <div className="flex items-center">
            <h1 className="text-16 font-semibold sm:text-[14px]">
              {outletData?.channelDefName || ""}
            </h1>
            {!!outletData.autoProcess && (
              <div className="flex items-center ml-5 p-4 pb-2 rounded-[4px] text-[#447E19] text-[8px] font-medium border-[0.5px] border-[#447E19]">
                AUTO PROCESS
              </div>
            )}
            {outletData.autoConfirm === "1" && (
              <div className="ml-5 p-4 pb-2 rounded-[4px] text-[#4780ED] text-[8px] font-medium border-[0.5px] border-[#4780ED]">
                AUTO CONFIRM
              </div>
            )}
            {updatedOutletsConfig?.showPill && isOutletUpdated ? (
              <p
                className={`text-10 text-[#397EF5] font-medium bg-[#F2F8FF] px-[6px] py-[2px] rounded-[3px] ml-[8px]`}
              >
                UPDATED
              </p>
            ) : null}
          </div>

          <p className="text-12 text-black/70 whitespace-nowrap overflow-hidden text-ellipsis">
            Outlet ID: {outletData?.id || "-"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`outlet-list-item flex items-center border border-[#E4E5EC] rounded-[12px]
      py-[18px] pl-[12px] pr-[52px] mb-[12px] last:mb-[40px] ${
        isOutletSelected ? "bg-[#0074FB]/10" : "bg-white"
      } relative hover:border-[#397EF5]
      ${parentClass || ""}
      ${bulkActionConfig?.active ? "cursor-pointer hover:bg-[#F6F8FC]" : ""}
      sm:rounded-[6px] sm:flex-col sm:items-start sm:px-0 sm:pb-0
    `}
      onClick={() => bulkActionConfig?.active && onOutletItemCheckboxClick()}
    >
      {bulkActionConfig?.active ? (
        <Checkbox
          checked={isOutletSelected}
          className="mr-[32px] ml-[20px] sm:m-0 sm:absolute sm: sm:bottom-[12px] sm:right-[12px]"
        />
      ) : null}
      <div
        className={`w-[calc(70%)] flex items-center mr-[20px] sm:w-full
        sm:items-start sm:justify-between sm:px-[10px] sm:mr-0 sm:pb-[16px]
        `}
      >
        <div
          className={`w-[calc(75%)] flex items-center mr-[20px] sm:flex-col
          sm:items-start sm:mr-[12px] sm:w-[70%]
          `}
        >
          {renderOutletLogoAndInfo()}
          {lastActivityText && renderLastActivityInfo()}
          {renderLastSyncInfo()}
        </div>
        {renderOutletOnlineStatusInfo()}
      </div>

      {bulkActionConfig?.active ? null : (
        <div
          className={`w-[calc(30%-20px)] flex items-center sm:w-full
          sm:border-t sm:border-[#DBDBDB] sm:pl-[10px] sm:py-[16px]
          sm:pr-[44px] sm:relative sm:whitespace-nowrap
        `}
        >
          <div className="flex flex-col items-center mr-[16px] w-[calc(33.3%-11px)]">
            <div
              className="rista-sprite ic-activity-log-black mb-[4px]"
              onClick={showActivityLog}
            ></div>
            <p className="text-12 text-[#8E8E8E] text-center sm:text-[14px]">
              Activity Log
            </p>
          </div>
          {isAdminUser() && (
            <div className="flex flex-col items-center mr-[16px] w-[calc(33.3%-11px)]">
              <div
                className="rista-sprite ic-sync-black mb-[4px]"
                onClick={onMenuSyncIconClick}
              ></div>
              <p className="text-12 text-[#8E8E8E] text-center sm:text-[14px]">
                Menu Sync
              </p>
            </div>
          )}
          {isAggregatorFeatureAvailable(
            AGGREGATOR_FEATURES_MAP.outlet_toggle.id,
            outletData?.type,
            outletData?.webhookSourceType
          ) ? (
            <Tooltip title={!isFeatureAllowed('AGGREGATOR_OUTLET_UPDATE') ? 'Current user does not have permission to take this action' : ''}>
            <div className={`flex flex-col items-center w-[calc(33.3%-11px)]`}>
              <div
                className="rista-sprite ic-outlet-toggle-black mb-[4px]"
                onClick={onOutletToggleIconClick}
              ></div>
              <p className="text-12 text-[#8E8E8E] text-center sm:text-[14px]">
                Outlet On/ Off
              </p>
            </div>
            </Tooltip>
          ) : null}
          {showOutletMenuIcon(
            outletData?.type,
            outletData?.webhookSourceType
          ) ? (
            <>
              <IconButton
                id={`outlet-more-options-menu-icon-${outletData?.id}`}
                aria-controls={
                  isMoreOptionsMenuOpen ? "outlet-more-options-menu" : undefined
                }
                aria-haspopup="true"
                aria-expanded={isMoreOptionsMenuOpen ? "true" : undefined}
                onClick={handleMoreOptionsIconClick}
                className="absolute right-[16px] sm:right-[4px]"
                title="More Options"
              >
                <div className="rista-sprite ic-kebab-menu-black"></div>
              </IconButton>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default OutletListItem;
